import { useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { inputCls } from "@/components/Filters";
import { PuntiVenditaForm } from "@/components/PuntiVendita";

const VUOTO = {
  codice_dealer: "", ragione_sociale: "", commerciale_slug: "", settore: "", stato: "ATTIVO",
  data_attivazione: "", data_chiusura: "", partita_iva: "", citta: "", cap: "", provincia: "", contatto: "", note: "",
};

const OBBLIGATORI = [
  ["codice_dealer", "codice dealer"], ["ragione_sociale", "ragione sociale"],
  ["commerciale_slug", "commerciale"], ["settore", "settore"], ["stato", "stato"],
  ["data_attivazione", "data attivazione"], ["provincia", "provincia"], ["cap", "CAP"],
];

const Campo = ({ label, children, obbligatorio }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-wider text-slate-500">
      {label}{obbligatorio && <span className="text-red-600"> *</span>}
    </span>
    {children}
  </label>
);

/** Form anagrafica convenzionato: creazione (nuovo) o modifica con decorrenza produzione. */
export default function DealerForm({ meta, dealer, preset, onSaved, onCancel, onSubmitOverride }) {
  const nuovo = !dealer;
  const m = meta || {};
  const [f, setF] = useState(nuovo ? { ...VUOTO, ...(preset || {}) } : { ...VUOTO, ...dealer, commerciale_slug: dealer.commerciale_slug || "" });
  const [decorrenza, setDecorrenza] = useState(new Date().toISOString().slice(0, 10));
  const [puntiVendita, setPuntiVendita] = useState([]);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const commercialeCambiato = !nuovo && f.commerciale_slug !== (dealer.commerciale_slug || "");

  const salvaRapido = async () => {
    setMsg(null);
    if (!String(f.codice_dealer || "").trim() || !String(f.ragione_sociale || "").trim()) {
      setMsg({ tipo: "err", testo: "Servono almeno codice convenzionato e ragione sociale" });
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/dealers?rapido=true", { ...f,
        commerciale_slug: f.commerciale_slug || null });
      setMsg({ tipo: "ok", testo: "Convenzionato creato · resta da completare l'anagrafica" });
      setF(VUOTO);
      onSaved?.(r.data);
    } catch (e) {
      setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore durante il salvataggio" });
    } finally { setSaving(false); }
  };

  const salva = async () => {
    setMsg(null);
    const richiesti = f.stato === "CHIUSO"
      ? [...OBBLIGATORI, ["data_chiusura", "data chiusura"]] : OBBLIGATORI;
    const mancanti = richiesti.filter(([k]) => !String(f[k] || "").trim()).map(([, l]) => l);
    if (mancanti.length) {
      setMsg({ tipo: "err", testo: `Campi obbligatori mancanti: ${mancanti.join(", ")}` });
      return;
    }
    setSaving(true);
    const body = { ...f, commerciale_slug: f.commerciale_slug || null, decorrenza,
      ...(nuovo ? { punti_vendita: puntiVendita.filter((p) => p.codice_pv?.trim()) } : {}) };
    if (onSubmitOverride) {
      try { await onSubmitOverride(body); } finally { setSaving(false); }
      return;
    }
    try {
      const r = nuovo ? await api.post("/dealers", body)
        : await api.put(`/dealers/${dealer.codice_dealer}`, body);
      const spostate = r.data.pratiche_spostate;
      setMsg({ tipo: "ok", testo: nuovo ? "Convenzionato creato"
        : `Anagrafica aggiornata${spostate ? ` · ${spostate} pratiche spostate dal ${decorrenza}` : ""}` });
      if (nuovo) setF(VUOTO);
      onSaved?.(r.data);
    } catch (e) {
      setMsg({ tipo: "err", testo: e.response?.data?.detail || "Errore durante il salvataggio" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 mb-6 border-2 border-slate-300" data-testid={nuovo ? "dealer-form-nuovo" : "dealer-form-modifica"}>
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-4">
        {nuovo ? "Nuovo convenzionato" : "Modifica anagrafica"}
        <span className="ml-2 normal-case tracking-normal text-slate-400">· i campi con * sono obbligatori</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Campo label="Codice dealer" obbligatorio>
          <input className={inputCls} value={f.codice_dealer} onChange={set("codice_dealer")}
            disabled={!nuovo} data-testid="dealer-f-codice" />
        </Campo>
        <Campo label="Ragione sociale" obbligatorio>
          <input className={inputCls} value={f.ragione_sociale} onChange={set("ragione_sociale")} data-testid="dealer-f-ragione" />
        </Campo>
        <Campo label="Commerciale" obbligatorio>
          <select className={inputCls} value={f.commerciale_slug} onChange={set("commerciale_slug")} data-testid="dealer-f-commerciale">
            <option value="">Non assegnato</option>
            {(m.commerciali || []).map((c) => <option key={c.slug} value={c.slug}>{c.nome}</option>)}
          </select>
        </Campo>
        <Campo label="Settore" obbligatorio>
          <select className={inputCls} value={f.settore || ""} onChange={set("settore")} data-testid="dealer-f-settore">
            <option value="">Non indicato</option>
            {(m.settori || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Campo>
        <Campo label="Stato" obbligatorio>
          <select className={inputCls} value={f.stato} onChange={set("stato")} data-testid="dealer-f-stato">
            <option value="ATTIVO">Attivo</option>
            <option value="CHIUSO">Chiuso</option>
          </select>
        </Campo>
        <Campo label="Data attivazione" obbligatorio>
          <input type="date" className={inputCls} value={f.data_attivazione || ""} onChange={set("data_attivazione")} data-testid="dealer-f-attivazione" />
        </Campo>
        {f.stato === "CHIUSO" && (
          <Campo label="Data chiusura" obbligatorio>
            <input type="date" className={inputCls} value={f.data_chiusura || ""}
              onChange={set("data_chiusura")} data-testid="dealer-f-chiusura" />
          </Campo>
        )}
        <Campo label="Partita IVA">
          <input className={inputCls} value={f.partita_iva || ""} onChange={set("partita_iva")} data-testid="dealer-f-piva" />
        </Campo>
        <Campo label="Città">
          <input className={inputCls} value={f.citta || ""} onChange={set("citta")} data-testid="dealer-f-citta" />
        </Campo>
        <Campo label="CAP" obbligatorio>
          <input className={inputCls} maxLength={5} value={f.cap || ""} onChange={set("cap")}
            placeholder="24040" data-testid="dealer-f-cap" />
        </Campo>
        <Campo label="Provincia (sigla)" obbligatorio>
          <input className={inputCls} maxLength={2} value={f.provincia || ""} onChange={set("provincia")}
            placeholder="BG" data-testid="dealer-f-provincia" />
        </Campo>
        <Campo label="Contatto (email o telefono)">
          <input className={inputCls} value={f.contatto || ""} onChange={set("contatto")} data-testid="dealer-f-contatto" />
        </Campo>
        <Campo label="Note">
          <input className={inputCls} value={f.note || ""} onChange={set("note")} data-testid="dealer-f-note" />
        </Campo>
      </div>
      {nuovo && <PuntiVenditaForm value={puntiVendita} onChange={setPuntiVendita} codiceDealer={f.codice_dealer} ragioneSociale={f.ragione_sociale} />}

      {commercialeCambiato && (
        <div className="mt-4 p-4 rounded-md border-2 border-amber-400 bg-amber-50/70" data-testid="dealer-f-decorrenza-box">
          <div className="text-sm text-amber-800">
            Stai spostando il convenzionato a un altro commerciale: la produzione verrà riattribuita
            <strong> dalla data di decorrenza in poi</strong>, quella precedente resta al commerciale attuale.
          </div>
          <div className="mt-3 max-w-[220px]">
            <Campo label="Data di decorrenza">
              <input type="date" className={inputCls} value={decorrenza}
                onChange={(e) => setDecorrenza(e.target.value)} data-testid="dealer-f-decorrenza" />
            </Campo>
          </div>
        </div>
      )}

      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button type="button" onClick={salva} disabled={saving} data-testid="dealer-f-salva"
          className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700 transition-colors duration-200 disabled:opacity-50">
          {saving ? "Salvataggio…" : nuovo ? "Crea convenzionato" : "Salva modifiche"}
        </button>
        {nuovo && (
          <button type="button" onClick={salvaRapido} disabled={saving} data-testid="dealer-f-rapido"
            className="px-4 py-2 rounded-md border-2 border-slate-900 text-slate-900 text-sm
              hover:bg-slate-900 hover:text-white transition-colors duration-200 disabled:opacity-50">
            Salva solo codice e nome
          </button>
        )}
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid="dealer-f-annulla"
            className="px-4 py-2 rounded-md border border-slate-200 text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200">
            Annulla
          </button>
        )}
        {msg && (
          <span data-testid="dealer-f-msg"
            className={`text-sm ${msg.tipo === "ok" ? "text-emerald-600" : "text-red-600"}`}>
            {msg.testo}
          </span>
        )}
      </div>
    </Card>
  );
}
